import { Users, Flame, ArrowUpCircle, Percent, DollarSign, TrendingUp } from "lucide-react";

import { PartnerCapabilities } from "../capabilities";
import StatRow, { Stat } from "../StatRow";
import { EARNINGS_NOTE, PROJECTION_NOTE } from "../InfoTip";
import {
  Member,
  Product,
  Projection,
  computeKpis,
  monthEarnings,
  formatCurrency,
} from "../../../utils/partnerMetrics";

interface KpiStripProps {
  members: Member[];
  products: Product[];
  capabilities: PartnerCapabilities;
  projection: Projection | null;
  projectionLoading?: boolean;
  loading?: boolean;
}

const KpiStrip = ({
  members,
  products,
  capabilities,
  projection,
  projectionLoading,
  loading,
}: KpiStripProps) => {
  const kpis = computeKpis(members);
  const stats: Stat[] = [
    { label: "Active Members", value: String(kpis.activeMembers), icon: Users },
    { label: "Trials", value: String(kpis.trials), icon: Flame },
    { label: "Upgrades", value: String(kpis.upgrades), icon: ArrowUpCircle },
    {
      label: "Conversion Rate",
      value: `${kpis.conversionRate}%`,
      icon: Percent,
      info: "Share of everyone who bought a trial at your studio and later upgraded here.",
    },
  ];

  if (capabilities.canViewRevenue) {
    const earnings = monthEarnings(members, products);
    stats.push({
      label: "Earnings This Month",
      value: formatCurrency(earnings.totalEarnings),
      icon: DollarSign,
      hint: earnings.awaitingCheckIn.count
        ? `+${formatCurrency(earnings.awaitingCheckIn.earnings)} awaiting check-in`
        : `${earnings.paidPeriods} checked-in membership${earnings.paidPeriods === 1 ? "" : "s"}`,
      info: EARNINGS_NOTE,
    });
  }

  if (capabilities.canViewProjections) {
    stats.push({
      label: "Projected Next Month",
      value: projectionLoading ? "…" : projection ? formatCurrency(projection.value) : "—",
      icon: TrendingUp,
      hint: projection
        ? `${projection.memberCount} member${projection.memberCount === 1 ? "" : "s"} upgrading or renewing`
        : undefined,
      info: PROJECTION_NOTE,
    });
  }

  return <StatRow stats={stats} loading={loading} />;
};

export default KpiStrip;
